import { useFlatRoof } from "../flat-roof/hooks"
import {
  BottomLayerTypes,
  InsulationTypes,
  RoofFloorTypes,
  RoofTypeClimateClasses,
  RoofTypeTypes,
  SecuringMethodTypes,
  TopLayerTypes,
  VaporShieldTypes,
  WaterproofingTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { parseMaterial } from "../../services/helpers.service"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { FlatRoofData } from "../flat-roof/store"
import {
  checkValidQueriedValueOfSecuringMethod,
  checkValidQueriedValueOfType,
} from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setRoofTypeClimateClass,
    setRoofTypeType,
    setRoofFloorType,
    setRoofFloorMaterial,
    setWaterproofingType,
    setSecuringMethodTopLayer,
    setTopLayerMaterial,
    setSecuringMethodBottomLayer,
    setBottomLayerMaterial,
    setSecuringMethodInsulation,
    setInsulationMaterial,
    setInsulationMaterialWithThickness,
    setSecuringMethodVaporShield,
    setVaporShieldMaterial,
    setSecuringMethodFinishing,
  } = useFlatRoof()

  const getEnumKey = (enumValue: string | null, EnumTypes: any) => {
    if (!enumValue) return null
    return Object.keys(EnumTypes)[Object.values(EnumTypes).indexOf(enumValue)]
  }

  const setQueryParams = (
    data: FlatRoofData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.FLAT_ROOF_5_OVERVIEW", { lng: language })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Roof type
    const roofTypeEnum = getEnumKey(data.roofType.type, RoofTypeTypes)
    if (roofTypeEnum) {
      url.searchParams.append("roof-type", roofTypeEnum)
    }

    // Climate class
    const climateClassEnum = getEnumKey(
      data.roofType.climateClass,
      RoofTypeClimateClasses
    )
    if (climateClassEnum) {
      url.searchParams.append("climate-class", climateClassEnum)
    }

    // Roof floor
    const roofFloorEnum = getEnumKey(data.roofFloor.type, RoofFloorTypes)
    if (roofFloorEnum) {
      url.searchParams.append("roof-floor", roofFloorEnum)
    }

    // Waterproofing
    const waterproofingEnum = getEnumKey(
      data.waterproofing.type,
      WaterproofingTypes
    )
    if (waterproofingEnum) {
      url.searchParams.append("waterproofing", waterproofingEnum)
    }

    if (data.securingMethods) {
      // Top layer
      const topLayer = data.securingMethods.topLayer
      const topLayerEnum = getEnumKey(topLayer?.type, TopLayerTypes)
      const topLayerSecuringMethodEnum = getEnumKey(
        topLayer?.method,
        SecuringMethodTypes
      )
      if (topLayerEnum && topLayerSecuringMethodEnum) {
        url.searchParams.append(
          "toplayer",
          `${topLayerEnum} / ${topLayerSecuringMethodEnum}`
        )
      }

      // Bottom layer
      const bottomLayer = data.securingMethods.bottomLayer
      const bottomLayerEnum = getEnumKey(bottomLayer?.type, BottomLayerTypes)
      const bottomLayerSecuringMethodEnum = getEnumKey(
        bottomLayer?.method,
        SecuringMethodTypes
      )
      if (bottomLayerEnum && bottomLayerSecuringMethodEnum) {
        url.searchParams.append(
          "bottomlayer",
          `${bottomLayerEnum} / ${bottomLayerSecuringMethodEnum}`
        )
      }

      // Insulation
      const insulation = data.securingMethods.insulation
      const insulationEnum = getEnumKey(insulation?.type, InsulationTypes)
      const insulationSecuringMethodEnum = getEnumKey(
        insulation?.method,
        SecuringMethodTypes
      )
      if (insulationEnum && insulationSecuringMethodEnum) {
        url.searchParams.append(
          "insulation",
          `${insulationEnum} / ${insulationSecuringMethodEnum}`
        )

        // Include insulation thickness
        const insulationThickness = data.thermalPerformance.insulation.thickness
        if (insulationThickness) {
          url.searchParams.append(
            "insulation-thickness",
            insulationThickness.toString()
          )
        }
      }

      // Vapor Shield
      const vaporShield = data.securingMethods.vaporShield
      const vaporShieldEnum = getEnumKey(vaporShield?.type, VaporShieldTypes)
      const vaporShieldSecuringMethodEnum = getEnumKey(
        vaporShield?.method,
        SecuringMethodTypes
      )
      if (vaporShieldEnum && vaporShieldSecuringMethodEnum) {
        url.searchParams.append(
          "vaporshield",
          `${vaporShieldEnum} / ${vaporShieldSecuringMethodEnum}`
        )
      }

      // Finishing
      const finishing = data.securingMethods.finishing
      if (finishing) {
        url.searchParams.append("finishing", finishing)
      }
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      roofType: url.searchParams.get("roof-type"),
      climateClass: url.searchParams.get("climate-class"),
      roofFloor: url.searchParams.get("roof-floor"),
      waterproofing: url.searchParams.get("waterproofing"),
      topLayer: url.searchParams.get("toplayer"),
      bottomLayer: url.searchParams.get("bottomlayer"),
      insulation: url.searchParams.get("insulation"),
      insulationThickness: url.searchParams.get("insulation-thickness"),
      vaporShield: url.searchParams.get("vaporshield"),
      finishing: url.searchParams.get("finishing"),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Roof Type
    const validRoofType = checkValidQueriedValueOfType(
      query.roofType,
      RoofTypeTypes
    )
    if (validRoofType) setRoofTypeType(validRoofType)

    // Climate Class
    const validClimateClass = checkValidQueriedValueOfType(
      query.climateClass,
      RoofTypeClimateClasses
    )
    if (validClimateClass) setRoofTypeClimateClass(validClimateClass)

    // Roof Floor
    const validRoofFloor = checkValidQueriedValueOfType(
      query.roofFloor,
      RoofFloorTypes
    )
    if (validRoofFloor) setRoofFloorType(validRoofFloor)
    // console.log(validRoofFloor)

    let roofFloorMaterial = null
    switch (validRoofFloor) {
      case RoofFloorTypes.WOOD:
        roofFloorMaterial = parseMaterial("ROOF_FLOOR_WOOD")
        break
      case RoofFloorTypes.CONCRETE:
        roofFloorMaterial = parseMaterial("ROOF_FLOOR_CONCRETE")
        break
      case RoofFloorTypes.STEEL_DECK:
        roofFloorMaterial = parseMaterial("ROOF_FLOOR_STEEL_DECK")
        break
    }
    if (roofFloorMaterial) setRoofFloorMaterial(roofFloorMaterial)

    // Waterproofing
    const validWaterproofing = checkValidQueriedValueOfType(
      query.waterproofing,
      WaterproofingTypes
    )
    if (validWaterproofing) setWaterproofingType(validWaterproofing)

    // Top layer
    const validTopLayer = checkValidQueriedValueOfSecuringMethod(
      query.topLayer,
      TopLayerTypes
    )
    if (validTopLayer) {
      setSecuringMethodTopLayer(validTopLayer)
      const topLayerMaterial = parseMaterial(validTopLayer.type)
      if (topLayerMaterial) setTopLayerMaterial(topLayerMaterial)
    }

    // Bottom layer
    const validBottomLayer = checkValidQueriedValueOfSecuringMethod(
      query.bottomLayer,
      BottomLayerTypes
    )
    if (validBottomLayer) {
      setSecuringMethodBottomLayer(validBottomLayer)
      const bottomLayerMaterial = parseMaterial(validBottomLayer.type)
      if (bottomLayerMaterial) setBottomLayerMaterial(bottomLayerMaterial)
    }

    // Insulation
    const validInsulation = checkValidQueriedValueOfSecuringMethod(
      query.insulation,
      InsulationTypes
    )
    if (validInsulation) {
      setSecuringMethodInsulation(validInsulation)
      const insulationMaterial = parseMaterial(validInsulation.type)
      if (insulationMaterial) {
        // Include insulation thickness
        if (query.insulationThickness) {
          setInsulationMaterialWithThickness(
            insulationMaterial,
            query.insulationThickness
          )
        } else {
          setInsulationMaterial(insulationMaterial)
        }
      }
    }

    // Vapor Shield
    const validVaporShield = checkValidQueriedValueOfSecuringMethod(
      query.vaporShield,
      VaporShieldTypes
    )
    if (validVaporShield) {
      setSecuringMethodVaporShield(validVaporShield)
      const vaporShieldMaterial = parseMaterial(validVaporShield.type)
      if (vaporShieldMaterial) setVaporShieldMaterial(vaporShieldMaterial)
    }

    // Finishing
    const validFinishing = query.finishing
    if (validFinishing) setSecuringMethodFinishing(validFinishing)

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.FLAT_ROOF)

    // Routing
    let route = Routes.FLAT_ROOF_1_ROOF_TYPE
    if (
      validRoofType &&
      // validClimateClass &&
      validRoofFloor &&
      validWaterproofing &&
      // validFinishing &&
      validTopLayer &&
      // validBottomLayer &&
      validInsulation &&
      validVaporShield
    ) {
      route = Routes.FLAT_ROOF_5_OVERVIEW
    } else if (
      validRoofType &&
      validClimateClass &&
      validRoofFloor &&
      validWaterproofing
    ) {
      route = Routes.FLAT_ROOF_3_WATERPROOFING
    } else if (validRoofType && validClimateClass && validRoofFloor) {
      route = Routes.FLAT_ROOF_2_ROOF_FLOOR
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
